'use client';

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useRouter, usePathname } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';

interface EditorSidebarProps {
  counts?: {
    myOrders?: number;
    inProgress?: number;
    completed?: number;
  };
}

const EditorSidebar: React.FC<EditorSidebarProps> = ({ counts = {} }) => {
  const router = useRouter();
  const pathname = usePathname();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => setMounted(true), []);

  useEffect(() => {
    const handler = () => setMobileOpen((o) => !o);
    window.addEventListener('toggle-sidebar', handler);
    return () => window.removeEventListener('toggle-sidebar', handler);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  const menuItems = [
    { icon: 'Squares2X2Icon', label: 'Dashboard', path: '/editor-workspace' },
    { icon: 'BriefcaseIcon', label: 'My Orders', path: '/editor-workspace', badge: counts.myOrders },
    { icon: 'ClockIcon', label: 'In Progress', path: '/editor-workspace', badge: counts.inProgress },
    { icon: 'CheckCircleIcon', label: 'Completed', path: '/editor-workspace', badge: counts.completed },
    { icon: 'ChatBubbleLeftRightIcon', label: 'Messages', path: '/editor-workspace/messages' },
    { icon: 'QuestionMarkCircleIcon', label: 'Support', path: '/contact' },
  ];

  const isActive = (path: string, label: string) =>
    label === 'Dashboard' ? pathname === '/editor-workspace' : pathname === path && label !== 'Dashboard';

  const go = (path: string) => {
    setMobileOpen(false);
    router.push(path);
  };

  const NavInner = () => (
    <>
      {/* Logo */}
      <div className="px-6 py-5 border-b border-white/[0.06] flex items-center justify-between">
        <button onClick={() => go('/homepage')} className="flex items-center group">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src="/shashio-logo.svg" alt="Shashio" className="h-7 w-auto transition-transform duration-300 group-hover:scale-105" />
        </button>
        <button
          onClick={() => setMobileOpen(false)}
          className="lg:hidden p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-white/[0.06] transition-colors"
          aria-label="Close menu"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-4 overflow-y-auto">
        <div className="space-y-1">
          {menuItems.map((item, i) => {
            const active = isActive(item.path, item.label) || (item.label === 'Dashboard' && pathname === '/editor-workspace' && i === 0);
            return (
              <button
                key={item.label}
                onClick={() => go(item.path)}
                className={`group relative w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-300 text-left ${
                  active
                    ? 'bg-gradient-to-r from-purple-600 to-purple-500 text-white shadow-lg shadow-purple-500/30'
                    : 'text-gray-400 hover:bg-white/[0.06] hover:text-white'
                }`}
              >
                <Icon name={item.icon as any} size={20} variant="outline" style={{ color: active ? '#FFFFFF' : 'currentColor' }} />
                <span className="font-medium text-sm flex-1">{item.label}</span>
                {typeof item.badge === 'number' && item.badge > 0 && (
                  <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${active ? 'bg-white/20 text-white' : 'bg-purple-500/20 text-purple-300'}`}>
                    {item.badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </nav>

      {/* Level up card */}
      <div className="p-3">
        <div className="relative overflow-hidden rounded-2xl border border-purple-500/20 bg-gradient-to-br from-purple-600/20 to-blue-600/10 p-4">
          <div className="absolute -top-8 -right-8 w-24 h-24 bg-purple-500/20 rounded-full blur-2xl" />
          <div className="relative">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center mb-2">
              <Icon name="TrophyIcon" size={18} variant="solid" style={{ color: '#FFFFFF' }} />
            </div>
            <p className="text-sm font-semibold text-white mb-1">Level Up Your Skills</p>
            <p className="text-xs text-gray-400 mb-3">Complete more orders to unlock higher levels and earn more.</p>
            <button
              onClick={() => go('/editor-workspace')}
              className="w-full py-2 rounded-lg text-xs font-bold bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 text-white transition-all"
            >
              View Progress
            </button>
          </div>
        </div>
      </div>
    </>
  );

  return (
    <>
      {/* Desktop sidebar */}
      <div
        className="hidden lg:flex w-64 h-screen flex-col border-r backdrop-blur-sm"
        style={{ backgroundColor: 'rgba(15, 16, 20, 0.85)', borderColor: 'rgba(31, 41, 55, 0.5)' }}
      >
        <NavInner />
      </div>

      {/* Mobile drawer */}
      {mounted && mobileOpen && createPortal(
        <div className="lg:hidden fixed inset-0 z-[9999]">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
          <div
            className="absolute left-0 top-0 bottom-0 w-72 max-w-[80vw] flex flex-col border-r shadow-2xl animate-editor-sidebar-in"
            style={{ backgroundColor: '#0B0D17', borderColor: 'rgba(31, 41, 55, 0.5)' }}
          >
            <NavInner />
          </div>
        </div>,
        document.body
      )}

      <style jsx global>{`
        @keyframes editor-sidebar-in {
          from { transform: translateX(-100%); }
          to { transform: translateX(0); }
        }
        .animate-editor-sidebar-in { animation: editor-sidebar-in 0.25s ease-out; }
      `}</style>
    </>
  );
};

export default EditorSidebar;
